import invariant from 'tiny-invariant'
import { isProduction } from 'utils/env'

// https://developers.openai.com/ads/measurement-pixel
export enum OpenAiEventName {
  REGISTRATION_COMPLETED = 'registration_completed',
  TRIAL_STARTED = 'trial_started',
}

export const OpenAiPlanId = {
  STUDIO_AI_FREE_TRIAL: 'studio_ai_free_trial',
  ALL_ACCESS_FREE_TRIAL: 'all_access_free_trial',
} as const

export type OpenAiPlanId = (typeof OpenAiPlanId)[keyof typeof OpenAiPlanId]

export type OpenAiEventOptions =
  | { type: 'customer_action'; amount?: number; currency?: string }
  | { type: 'plan_enrollment'; plan_id: OpenAiPlanId }

export interface OpenAiUserData {
  email_sha256?: string
  external_id_sha256?: string
}

export interface OpenAiPixelFunction {
  (command: 'init', options: { pixelId: string; user: OpenAiUserData }): void
  (command: 'measure', name: OpenAiEventName, options: OpenAiEventOptions): void
}

declare global {
  interface Window {
    oaiq?: OpenAiPixelFunction
  }
}

export const openAiPixelId =
  process.env.NEXT_PUBLIC_OPENAI_PIXEL_ID || process.env.VITE_OPENAI_PIXEL_ID

invariant(
  openAiPixelId,
  'NEXT_PUBLIC_OPENAI_PIXEL_ID or VITE_OPENAI_PIXEL_ID must be set inside OpenAiPixel',
)

export const scriptUrl = 'https://bzrcdn.openai.com/sdk/oaiq.min.js'

// Iubenda may hold the init script until consent, so React can't assume `window.oaiq` exists
// when its effects run.
export const openAiPixelReadyEventName = 'cf:openai-pixel-ready'

export const initScript = `!function(w,d,s,u){if(w.oaiq)return;var q=function(){q.q.push(arguments)};q.q=[];w.oaiq=q;var js=d.createElement(s);js.async=true;js.src=u;var f=d.getElementsByTagName(s)[0];f.parentNode.insertBefore(js,f)}(window,document,"script","${scriptUrl}");oaiq("init",{pixelId:"${openAiPixelId}"});window.dispatchEvent(new Event("${openAiPixelReadyEventName}"));`

async function sha256Hex(value: string) {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value))
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('')
}

// Normalization rules: https://developers.openai.com/ads/measurement-pixel#send-user-data
export async function hashOpenAiUser({
  email,
  externalId,
}: {
  email?: string | null
  externalId?: string | null
}): Promise<OpenAiUserData> {
  const normalizedEmail = email?.trim().toLowerCase()
  const normalizedExternalId = externalId?.trim()
  const [emailSha256, externalIdSha256] = await Promise.all([
    normalizedEmail ? sha256Hex(normalizedEmail) : undefined,
    normalizedExternalId ? sha256Hex(normalizedExternalId) : undefined,
  ])

  return {
    ...(emailSha256 ? { email_sha256: emailSha256 } : {}),
    ...(externalIdSha256 ? { external_id_sha256: externalIdSha256 } : {}),
  }
}

export function isOpenAiPixelReady() {
  return isProduction && typeof window.oaiq === 'function'
}

/** User data is page-scoped in the pixel, so it is sent on `init` rather than per `measure`. */
export function setOpenAiUser(user: OpenAiUserData) {
  if (!openAiPixelId || !isOpenAiPixelReady()) {
    return
  }

  window.oaiq?.('init', { pixelId: openAiPixelId, user })
}

function measure(name: OpenAiEventName, options: OpenAiEventOptions) {
  if (!isOpenAiPixelReady()) {
    return
  }

  window.oaiq?.('measure', name, options)
}

export function trackOpenAiRegistration() {
  measure(OpenAiEventName.REGISTRATION_COMPLETED, { type: 'customer_action' })
}

export function trackOpenAiTrialStarted(planId: OpenAiPlanId) {
  measure(OpenAiEventName.TRIAL_STARTED, { type: 'plan_enrollment', plan_id: planId })
}
